import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { BaseUI } from '../base.ui';
import { ParentEvents } from '../../utils/parent-events.service';

let nextUniqueId = 0;

@Component({
    selector: 'thr-file-selector',
    templateUrl: './file-selector.component.html',
    styleUrls: ['./file-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileSelectorComponent),
            multi: true,
        },
    ],
})
export class FileSelectorComponent extends BaseUI implements AfterContentInit, AfterViewInit, ControlValueAccessor, OnDestroy {
    private _uniqueId = `thr-file-${nextUniqueId++}`;

    /** The HTML name attribute applied to input file. */
    private _name = this._uniqueId;

    /** Whether the `value` has been set to its initial value. */
    private _isInitialized = false;

    /** Whether the file selector is disabled. */
    private _disabled = false;

    /** Whether the file selector is readonly. */
    private _readonly = false;

    /** Whether the file selector is required. */
    private _required = false;

    /** Value assigned to this file selector. */
    private _value: File = null;

    @Input()
    id: string = this._uniqueId;

    @Input()
    label: string;

    @Input()
    filename: string;

    @Input()
    accept = 'image/jpeg,image/png,application/pdf';

    @Input()
    canView = false;

    @Input()
    canRemove = true;

    @Input()
    iconPath: string;

    @Input()
    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    @Input()
    get value(): File {
        return this._value;
    }

    set value(newValue: File) {
        if (this._value !== newValue) {
            this._value = newValue;
        }

        if (!this.filename && this._value) {
            this.filename = this._value.name;
        }
    }

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this._cdr.markForCheck();
    }

    @Input()
    get readonly(): boolean {
        return this._readonly;
    }

    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
        this._cdr.markForCheck();
    }

    /** Whether the radio group is required */
    @Input()
    get required(): boolean {
        return this._required;
    }

    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this._cdr.markForCheck();
    }

    // tslint:disable-next-line:no-output-native
    @Output()
    readonly change: EventEmitter<File> = new EventEmitter<File>();

    @Output()
    readonly view: EventEmitter<void> = new EventEmitter();

    @Output()
    readonly remove: EventEmitter<void> = new EventEmitter();

    onTouched: () => void = () => {};
    onChange: (value: File) => void = () => {};

    constructor(parentEvents: ParentEvents, private _el: ElementRef, private _cdr: ChangeDetectorRef) {
        super(parentEvents);
    }

    ngAfterContentInit() {
        this._isInitialized = true;
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
    }

    emitChangeEvent(): void {
        if (this._isInitialized) {
            this.change.emit(this.value);
        }
    }

    emitViewEvent(): void {
        if (this._isInitialized) {
            this.view.emit();
        }
    }

    emitRemoveEvent(): void {
        if (this._isInitialized) {
            this.remove.emit();
        }
    }

    /**
     * Sets the model value. Implemented as part of ControlValueAccessor.
     */
    writeValue(value: File) {
        this.value = value;
        this._cdr.markForCheck();
    }

    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     */
    registerOnChange(fn: (value: File) => void) {
        this.onChange = fn;
    }

    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     */
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    /**
     * Sets the disabled state of the control.
     * Implemented as a part of ControlValueAccessor.
     */
    setDisabledState(isDisabled: boolean) {
        const newDisabledState = coerceBooleanProperty(isDisabled);
        if (this._disabled !== newDisabledState) {
            this._disabled = newDisabledState;
            this._cdr.markForCheck();
        }
    }

    onInputChange(event: Event) {
        if (event.target instanceof HTMLInputElement) {
            const file = event.target.files.item(0);

            if (file instanceof File) {
                this.writeValue(file);
                this.filename = file.name;

                this.onChange(this.value);
                this.onTouched();
                this.emitChangeEvent();
            }
        }
    }

    onView() {
        this.emitViewEvent();
    }

    onRemove() {
        this.writeValue(undefined);
        this.filename = undefined;

        this.onChange(this.value);
        this.onTouched();
        this.emitRemoveEvent();
    }

    onSelect() {
        const inputFile = this._el.nativeElement.querySelector('input');
        inputFile.value = null;
        inputFile.click();
    }
}
